import {
  Applicant,
  BlockType,
  defaultApplicant,
  PageType,
  RelationshipType,
  Section,
  SectionElementType,
} from '@common/entities';
import { ModalApplicationStore } from 'state/modalApplicationReducer';

import type { Block, Field } from 'types/fields';
import type { Form } from 'types/form';
import type { AnyPage } from 'types/page';

import { getTodaysDate } from './date';
import { getActiveResponse } from './getActiveResponse';

const filterOutElements = (
  elements: Array<Block | Field>,
  secondaryApplicantType: RelationshipType,
) => {
  return elements.filter((element) => {
    const isNovaEligibilityCompass =
      element.elementType === SectionElementType.Block &&
      element.customType === BlockType.NovaEligibilityCompass;
    const { elementType } = element;
    const hideFromCoApplicant =
      elementType === SectionElementType.Block &&
      !isNovaEligibilityCompass &&
      ((secondaryApplicantType === RelationshipType.Coapplicant &&
        element?.configuration?.hideFromCoapplicant) ||
        (secondaryApplicantType === RelationshipType.Guarantor &&
          element?.configuration?.hideFromGuarantor));
    return hideFromCoApplicant ? false : element;
  });
};

const filterOutSections = (sections: Section[], secondaryApplicantType: RelationshipType) => {
  return sections.map((section) => {
    return {
      ...section,
      elements: filterOutElements(section.elements, secondaryApplicantType),
    };
  });
};

const getCoApplicantPages = (pagesArr: AnyPage[], secondaryApplicantType: RelationshipType) =>
  pagesArr.reduce((acc: any, page: any) => {
    const sections = filterOutSections(page.sections, secondaryApplicantType);
    const visibleSections: any[] = [];
    sections.forEach((section) => {
      const visibleElements = filterOutElements(section.elements, secondaryApplicantType);
      if (visibleElements?.length > 0) {
        visibleSections.push(section);
      }
    });
    const hiddenByFlag =
      (secondaryApplicantType === RelationshipType.Coapplicant &&
        page?.configuration?.hideFromCoApplicant) ||
      (secondaryApplicantType === RelationshipType.Guarantor &&
        page?.configuration?.hideFromGuarantor);

    const hidePage =
      page?.type === PageType.FloorPlans
        ? true
        : Boolean(hiddenByFlag) ||
          (visibleSections.length === 0 &&
            page?.type !== PageType.Summary &&
            page?.type !== PageType.Payment);

    if (!hidePage) {
      return [
        ...acc,
        {
          ...page,
          sections: visibleSections,
        },
      ];
    }
    return acc;
  }, []);

export const getSecondaryApplicantStoreJson = (
  form: Form,
  applicant: Applicant,
  secondaryResponseId: string,
  secondaryApplicantType: RelationshipType = RelationshipType.Coapplicant,
): ModalApplicationStore => {
  const { pages } = form;
  const updatedPages = getCoApplicantPages(pages, secondaryApplicantType);
  const activeResponse = getActiveResponse(applicant, secondaryResponseId);

  return {
    response: { value: [] },
    leadProfile: {
      primaryResponseId: '',
      primaryApplicant: defaultApplicant,
      applicationForm: {
        id: '',
        name: '',
        description: '',
        type: '',
        clientId: 0,
        pages: [],
        versionId: '',
        applicationId: '',
        occupancyType: '',
      },
      applicants: [],
      applicantResponses: [],
    },
    fieldSettings: {
      disabledFields: [],
      isApplicationLocked: false,
    },
    application: {
      applicant: defaultApplicant,
      form: { ...form, pages: updatedPages },
      activeResponse,
    },
    navigation: {
      currentTabIndex: 0,
      activeStepByTab: {},
      stepStatus: {},
      leasingJourneyErrors: Array.from(Array(updatedPages?.length).values()),
      modalNavigationActiveStep: 0,
      previewModeActiveStep: 0,
    },
    payment: {
      paymentTypes: [],
      selectedPaymentType: null,
      paymentInfo: {},
      savePaymentInfo: false,
      termsAndConditionAck: false,
      paymentAccount: null,
      paymentSettings: {},
      error: [],
      moneyGramAccountInfo: null,
    },
    shoppingCart: {
      charges: [],
      scheduledChargesLoaded: false,
      isSyncingCharges: false,
      scheduledChargesSyncError: false,
      scheduledChargesRetryCount: 0,
      scheduledChargesRetryLimitReached: false,
    },
    unitSelection: {
      floorPlan: null,
      unit: null,
      leaseTerm: null,
      addOns: [],
      selectedSpecials: [],
      waitlistSelections: null,
      moveInDate: getTodaysDate(),
      quote: null,
      quoteLeaseTerm: null,
      propertySpecials: null,
      floorplanSpecials: null,
      unitSpaceSpecials: null,
      unitTypeSpecials: null,
      promoCodeSpecials: null,
      waitlistOffer: null,
      quoteUnitSpaceId: null,
    },
    userPermission: {
      permission: {},
    },
    pets: {
      selectedPets: [],
    },
    notifications: {
      queue: [],
      drawerOpen: false,
    },
  };
};
